import { LandingFooter } from "@/components/public/LandingFooter"
import { PublicHeader } from "@/components/public/PublicHeader"
import { Button } from "@/components/ui/button"
import { BreathingGradient } from "@/components/ui/living-brand"
import { SpotlightCard } from "@/components/ui/SpotlightCard"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { Page } from "@supabank/ui"
import { ArrowRight, Building2, CreditCard, QrCode, Wallet2 } from "lucide-react"
import { Link } from "react-router-dom"

type Product = {
  id: string
  title: string
  description: string
  href: string
  cta: string
  icon: React.ComponentType<{ className?: string }>
  features: string[]
  useCases: string[]
}

const products: Product[] = [
  {
    id: "liquidation-address",
    title: "Auto-Convert Crypto to Fiat",
    description:
      "Send USDC or USDT to your address. We automatically convert it to EUR or USD and deposit it in your bank. No manual work.",
    href: "/products/liquidation-address",
    cta: "Learn More",
    icon: CreditCard,
    features: [
      "Permanent crypto addresses",
      "Automatic conversion",
      "Multi-chain support",
      "Instant bank deposits",
    ],
    useCases: [
      "Receive crypto payments",
      "Automated payroll",
      "Merchant settlements",
    ],
  },
  {
    id: "virtual-account",
    title: "Receive Fiat. Get Crypto. Automatically.",
    description:
      "Get your own bank account details (IBAN, routing number). Send fiat from any bank. We automatically convert it to USDC or USDT and deliver it to your wallet.",
    href: "/products/virtual-account",
    cta: "Learn More",
    icon: Building2,
    features: [
      "Permanent bank accounts",
      "IBAN, routing, CLABE",
      "Automatic crypto delivery",
      "Multi-currency support",
    ],
    useCases: [
      "Fiat on-ramp",
      "Bank deposits",
      "Multi-currency accounts",
    ],
  },
  {
    id: "custodial-wallet",
    title: "Multi-Currency Wallet",
    description:
      "Hold USD, EUR, USDC, USDT, and more in one secure wallet. View balances, track transactions, and transfer funds instantly.",
    href: "/products/wallet",
    cta: "Learn More",
    icon: Wallet2,
    features: [
      "Multi-currency balances",
      "Real-time updates",
      "Transaction history",
      "Secure custodial storage",
    ],
    useCases: [
      "Hold multiple currencies",
      "Track transactions",
      "Internal transfers",
    ],
  },
  {
    id: "payout-routes",
    title: "Automated Payout Routes",
    description:
      "Link your liquidation address to your bank account. Every crypto deposit automatically converts and sends to your bank—no manual steps.",
    href: "/products/payout-routes",
    cta: "Learn More",
    icon: ArrowRight,
    features: [
      "One-time setup",
      "Automatic routing",
      "SEPA, ACH, Wire, PIX support",
      "Zero manual intervention",
    ],
    useCases: [
      "Automated settlements",
      "Recurring payouts",
      "Merchant payouts",
    ],
  },
  {
    id: "pix-payments",
    title: "PIX Instant Payments (Brazil)",
    description:
      "Accept and send payments instantly in Brazil using PIX. Fully integrated with our crypto-to-fiat engine for seamless BRL liquidity.",
    href: "/products/payout-routes", // Using payout routes as entry point for PIX config
    cta: "Learn More",
    icon: QrCode,
    features: [
      "Instant 24/7 settlement",
      "QR code generation",
      "CPF/CNPJ keys support",
      "Direct BRL <-> Stablecoin",
    ],
    useCases: [
      "Brazil market access",
      "Real-time BRL payouts",
      "Cross-border remittance",
    ],
  }
]

export function ProductsOverview() {
  return (
    <Page className="flex flex-col min-h-screen bg-background">
      {/* Unified PublicHeader component */}
      <PublicHeader />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className={cn(
          "relative overflow-hidden",
          "py-16 sm:py-24 xl:py-32", // Standardized spacing
          // CANON: Hero gradient background (lavenderGlow for fintech feel)
          semanticTokens.heroGradient.lavenderGlow.tailwind,
          // Dark mode variant
          "dark:bg-black dark:bg-[radial-gradient(ellipse_at_20%_50%,hsla(270,95%,75%,0.08)_0%,transparent_50%),radial-gradient(ellipse_at_80%_80%,hsla(217,91%,60%,0.06)_0%,transparent_50%),hsl(var(--background))]"
        )}>
          {/* Living Brand: Breathing Gradient (syncs with LED heartbeat) - DISABLED on dark theme */}
          <BreathingGradient
            variant="lavender"
            syncWithLed
            className="absolute inset-0 z-[1] pointer-events-none opacity-30 dark:hidden"
          />
          <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-4 sm:space-y-6 text-center">
              <h1 className={cn(
                "font-display text-display-sm md:text-display-md", // Standardized typography
                semanticTokens.fg.default
              )}>
                Product Suite
              </h1>
              <p className={cn(
                "mx-auto max-w-3xl text-lg sm:text-xl",
                semanticTokens.fg.muted
              )}>
                Everything you need to receive, hold, and send money—crypto and fiat, automatically converted and routed.
              </p>
            </div>
          </div>
        </section>

        {/* Products Grid */}
        <section className={cn(
          "relative overflow-hidden py-16 sm:py-24", // Standardized spacing
          semanticTokens.harmony.calm.bg
        )}>
          {/* Color Lab: Bold Blue-Purple Mesh gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse at 20% 20%, hsl(220 65% 50% / 0.15) 0%, transparent 50%),
                radial-gradient(ellipse at 80% 80%, hsl(270 60% 72% / 0.12) 0%, transparent 50%),
                radial-gradient(ellipse at 50% 50%, hsl(210 65% 48% / 0.08) 0%, transparent 60%)
              `
            }}
          />
          {/* Living Brand: Breathing Gradient (subtle, syncs with LED heartbeat) */}
          <BreathingGradient
            variant="blue"
            syncWithLed
            className="absolute inset-0 z-[1] pointer-events-none opacity-20"
          />
          <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
            <div className="grid grid-cols-1 gap-6 sm:gap-8 md:grid-cols-2 lg:grid-cols-3">
              {products.map((product) => {
                const Icon = product.icon
                return (
                  <SpotlightCard
                    key={product.id}
                    className={cn(
                      "flex flex-col hover:shadow-xl hover:-translate-y-1",
                      "p-6 sm:p-8 h-full",
                      semanticTokens.motion.standard
                    )}
                  >
                    <div className="space-y-4 sm:space-y-5 flex-grow">
                      <div className="flex items-start gap-4">
                        <div className={cn(
                          "flex-shrink-0 rounded-xl p-3",
                          semanticTokens.bg.muted
                        )}>
                          <Icon className={cn(
                            "h-6 w-6 sm:h-7 sm:w-7",
                            semanticTokens.fg.default
                          )} />
                        </div>
                        <div className="flex-1 space-y-2">
                          <h2 className={cn(
                            "font-display text-heading-md sm:text-heading-lg",
                            semanticTokens.fg.default
                          )}>
                            {product.title}
                          </h2>
                          <p className={cn(
                            "text-sm sm:text-base leading-relaxed",
                            semanticTokens.fg.muted
                          )}>
                            {product.description}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-3 pt-4 border-t" style={{ borderColor: semanticTokens.border.subtle }}>
                        <div>
                          <h3 className={cn(
                            "text-sm font-semibold mb-2",
                            semanticTokens.fg.default
                          )}>
                            Key Features
                          </h3>
                          <ul className="space-y-1.5">
                            {product.features.map((feature) => (
                              <li key={feature} className={cn(
                                "flex items-start gap-2 text-sm",
                                semanticTokens.fg.muted
                              )}>
                                <span className={cn(
                                  "mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0",
                                  semanticTokens.accent.primary
                                )} />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h3 className={cn(
                            "text-sm font-semibold mb-2",
                            semanticTokens.fg.default
                          )}>
                            Use Cases
                          </h3>
                          <ul className="space-y-1.5">
                            {product.useCases.map((useCase) => (
                              <li key={useCase} className={cn(
                                "flex items-start gap-2 text-sm",
                                semanticTokens.fg.muted
                              )}>
                                <span className={cn(
                                  "mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0",
                                  semanticTokens.accent.primary
                                )} />
                                <span>{useCase}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t" style={{ borderColor: semanticTokens.border.subtle }}>
                      <Button asChild variant="outline" className="w-full sm:w-auto min-h-[44px] touch-manipulation">
                        <Link to={product.href}>
                          {product.cta}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </SpotlightCard>
                )
              })}
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section className={cn(
          "py-16 sm:py-24", // Standardized spacing
          semanticTokens.bg.surface
        )}>
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-6 sm:space-y-8">
              <div className="text-center space-y-3">
                <h2 className={cn(
                  "font-display text-display-sm md:text-display-md", // Standardized typography
                  semanticTokens.fg.default
                )}>
                  Which Product is Right for You?
                </h2>
                <p className={cn(
                  "mx-auto max-w-2xl text-base sm:text-lg",
                  semanticTokens.fg.muted
                )}>
                  Choose based on your needs
                </p>
              </div>

              <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                <table className="w-full border-collapse min-w-[600px] sm:min-w-0">
                  <thead>
                    <tr className={cn("border-b", semanticTokens.border.default)}>
                      <th className={cn(
                        "text-left p-4 font-semibold",
                        semanticTokens.fg.default
                      )}>
                        Feature
                      </th>
                      {products.slice(0, 4).map((product) => ( // Show first 4 core products in comparison
                        <th key={product.id} className={cn(
                          "text-center p-4 font-semibold",
                          semanticTokens.fg.default
                        )}>
                          {product.title}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className={cn("border-b", semanticTokens.border.subtle)}>
                      <td className={cn("p-4 font-medium", semanticTokens.fg.default)}>
                        Direction
                      </td>
                      <td className="p-4 text-center">Crypto → Fiat</td>
                      <td className="p-4 text-center">Fiat → Crypto</td>
                      <td className="p-4 text-center">Hold & Manage</td>
                      <td className="p-4 text-center">Configuration</td>
                    </tr>
                    <tr className={cn("border-b", semanticTokens.border.subtle)}>
                      <td className={cn("p-4 font-medium", semanticTokens.fg.default)}>
                        Setup Required
                      </td>
                      <td className="p-4 text-center">One-time</td>
                      <td className="p-4 text-center">One-time</td>
                      <td className="p-4 text-center">Automatic</td>
                      <td className="p-4 text-center">One-time</td>
                    </tr>
                    <tr className={cn("border-b", semanticTokens.border.subtle)}>
                      <td className={cn("p-4 font-medium", semanticTokens.fg.default)}>
                        Automation
                      </td>
                      <td className="p-4 text-center">Full</td>
                      <td className="p-4 text-center">Full</td>
                      <td className="p-4 text-center">Real-time</td>
                      <td className="p-4 text-center">Full</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={cn(
          "relative overflow-hidden py-16 sm:py-24", // Standardized spacing
          semanticTokens.harmony.calm.bg
        )}>
          {/* Color Lab: Subtle Blue-Lavender gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse at 10% 90%, hsla(217, 91%, 60%, 0.10) 0%, transparent 45%),
                radial-gradient(ellipse at 90% 10%, hsla(270, 95%, 75%, 0.08) 0%, transparent 45%)
              `
            }}
          />
          <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 text-center space-y-6">
            <h2 className={cn(
              "font-display text-display-sm md:text-display-md", // Standardized typography
              semanticTokens.fg.default
            )}>
              Ready to Get Started?
            </h2>
            <p className={cn(
              "text-base sm:text-lg",
              semanticTokens.fg.muted
            )}>
              Join builders who are automating financial flows
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="min-h-[44px] touch-manipulation">
                <Link to="/waitlist">
                  Get Your Address
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="min-h-[44px] touch-manipulation">
                <Link to="/how-payments-work">
                  Learn How It Works
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter />
    </Page>
  )
}

